window['ThemeSection_ProductFixedBar'] = ({
  variant,
  variantMedia,
  fallbackImage,
  fallbackImageAlt,
  buttonLabel,
  productFormId,
  ctaSelector,
  footerSelector,
}) => {
  // The main product form reports back through 'shapes:modalcart:afteradditem'.
  // If the request fails that event never fires, so the pending flag is released
  // by this timeout instead of staying stuck.
  const pendingAddTimeout = 8000;

  return {
    current_variant: variant,
    variantMedia: variantMedia || {},
    passedCta: false,
    footerVisible: false,
    loading: false,
    openCartOnAdd: false,
    observers: [],
    pendingAddTimeoutId: null,
    get available() {
      if (this.current_variant) {
        return this.current_variant.available === true;
      } else {
        return false;
      }
    },
    get price() {
      if (this.current_variant) {
        return this.current_variant.price;
      } else {
        return 0;
      }
    },
    get onSale() {
      if (this.current_variant && this.current_variant.compare_at_price) {
        return this.current_variant.compare_at_price > this.current_variant.price;
      } else {
        return false;
      }
    },
    get currentMedia() {
      if (this.current_variant) {
        return this.variantMedia[this.current_variant.id] || null;
      } else {
        return null;
      }
    },
    get image() {
      return (this.currentMedia && this.currentMedia.img) || fallbackImage;
    },
    get imageAlt() {
      return (this.currentMedia && this.currentMedia.alt) || fallbackImageAlt;
    },
    get ctaLabel() {
      if (!this.current_variant) {
        return window.theme.strings.unavailable;
      }
      if (this.loading) {
        return window.theme.strings.loading;
      }
      if (!this.current_variant.available) {
        return window.theme.strings.soldOut;
      }
      return buttonLabel || window.theme.strings.addToCart;
    },
    get overlayOpen() {
      const modals = Alpine.store('modals');

      if (!modals) return false;

      return (
        modals.leftDrawer.open ||
        modals.rightDrawer.open ||
        modals.modal.open ||
        modals.popup.open
      );
    },
    get visible() {
      return this.passedCta && !this.footerVisible && !this.overlayOpen;
    },
    formatMoney(price) {
      return formatMoney(price, theme.moneyFormat);
    },
    init() {
      // The main product component owns the current variant and announces every
      // change on its own root, bubbling up to the document (assets/product.js).
      this.onVariantChange = (evt) => {
        if (evt.detail && 'variant' in evt.detail) {
          this.current_variant = evt.detail.variant || null;
        }
      };
      document.addEventListener(
        'shapes:product:variantchange',
        this.onVariantChange
      );

      this.onAfterAddItem = this.afterAddItem.bind(this);
      document.body.addEventListener(
        'shapes:modalcart:afteradditem',
        this.onAfterAddItem
      );

      // Third party launchers (Shopify Inbox) sit in the same corner as the
      // CTA, so the body carries the bar state for CSS to move them out of it.
      this.$watch('visible', (value) => {
        document.body.classList.toggle('lyfta-fixed-bar-on', value);
      });

      this.$nextTick(() => this.mountObservers());
    },
    destroy() {
      document.body.classList.remove('lyfta-fixed-bar-on');
      this.observers.forEach((observer) => observer.disconnect());
      this.observers = [];
      window.clearTimeout(this.pendingAddTimeoutId);
      document.removeEventListener(
        'shapes:product:variantchange',
        this.onVariantChange
      );
      document.body.removeEventListener(
        'shapes:modalcart:afteradditem',
        this.onAfterAddItem
      );
    },
    mountObservers() {
      if (!('IntersectionObserver' in window)) return;

      const cta = document.querySelector(ctaSelector);
      const footer = document.querySelector(footerSelector);

      // The bar only shows once the main add to cart button has been scrolled
      // completely past, so the direction it left the viewport in matters.
      if (cta) {
        const ctaObserver = new window.IntersectionObserver(
          ([entry]) => {
            const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
            this.passedCta =
              !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
          },
          { threshold: 0 }
        );
        ctaObserver.observe(cta);
        this.observers.push(ctaObserver);
      }

      if (footer) {
        const footerObserver = new window.IntersectionObserver(
          ([entry]) => {
            this.footerVisible = entry.isIntersecting;
          },
          { threshold: 0 }
        );
        footerObserver.observe(footer);
        this.observers.push(footerObserver);
      }
    },
    afterAddItem() {
      window.clearTimeout(this.pendingAddTimeoutId);
      this.loading = false;

      // Only the additions started from this bar open the cart drawer, the main
      // buy button keeps the behaviour configured in the theme settings.
      if (!this.openCartOnAdd) return;

      this.openCartOnAdd = false;

      const modals = Alpine.store('modals');
      if (!modals) return;

      // Wait for the cart sections to be morphed in by the cart modules, they
      // listen for the same event one bubbling step later (assets/cart-items.js).
      this.$nextTick(() => modals.open('cart'));
    },
    addToCart() {
      if (!this.available || this.loading) return;

      // Delegating to the main product form keeps quantity, selling plan and
      // every other form input in sync with what the customer selected.
      const form = document.getElementById(productFormId);
      if (!form) return;

      this.loading = true;
      this.openCartOnAdd = true;

      window.clearTimeout(this.pendingAddTimeoutId);
      this.pendingAddTimeoutId = window.setTimeout(() => {
        this.loading = false;
        this.openCartOnAdd = false;
      }, pendingAddTimeout);

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    },
  };
};
